import React, { useEffect, useMemo, useState } from 'react'
import {
  Frames,
  CardNumber,
  ExpiryDate,
  Cvv,
  FrameCardTokenizedEvent,
} from 'frames-react'
import {
  Button,
  makeStyles,
  Typography,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core'
import useCommonStyles from '../../components/PaymentForm/CardForm.style'
import clsx from 'clsx'
import CMSText from '../Global/CMSText'
import { PrismicFunnelData } from '@/interfaces/common/prismicFunnel'
import { TFunction } from 'react-i18next'
import { PrismicGeneralData } from '@/interfaces/common/prismicGeneralConfig'
import { postDadPayment } from '@/redux/actions/payment'
import { connect } from 'react-redux'
import { RootState } from '@/redux/reducers/rootReducer'
import Loader from '../Loader/Loader'
import { createCheckoutPayment } from '@/redux/actions/checkout'
import {
  clearItemVariantFromGAParamsIfNotChalhoub,
  getPaymentInfoGtmData,
  isChalhoub,
  safeDataLayerPush,
} from '@/utils/gtag'
import * as gtag from '../../utils/gtag'
import { numberToPrice } from '@/utils/utils'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import { useRouter } from 'next/router'
import { GAEvent } from '@/typings/base'

const useStyles = makeStyles((theme) => ({
  formContainer: {
    backgroundColor: '#fff',
  },
  inputField: {
    marginBottom: theme.spacing(2),
    height: 38,
    color: '#000',
  },
  cardDetails: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  payButtonContainer: {
    marginBottom: theme.spacing(6),
  },
  payButton: {
    width: '100%',
  },
  loaderContainer: {
    height: 300,
    width: '100%',
    alignContent: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}))

const framesStyles = {
  base: {
    color: '#000',
  },
  invalid: {
    color: '#eb1c26',
  },
  placeholder: {
    base: {
      fontFamily: 'sans-serif',
      color: '#555',
    },
  },
}

const mapStateToProps = (state: RootState) => ({
  cart: state.cart,
  payment: state.payment,
})

const mapDispatchToProps = {
  postDadPayment: postDadPayment,
  createCheckoutPayment: createCheckoutPayment,
}

type TPaymentFormCheckoutProps = {
  publicKey: string
  text: PrismicFunnelData
  t: TFunction<'translation', undefined>
  settings: PrismicGeneralData
  coupon?: string
  swikId?: string
  disabled?: boolean
} & ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps

function PaymentFormCheckout({
  publicKey,
  t,
  text,
  settings,
  cart,
  postDadPayment,
  createCheckoutPayment,
  coupon,
  swikId,
  disabled,
}: TPaymentFormCheckoutProps) {
  const classes = useStyles()
  const commonClasses = useCommonStyles()
  const [framesReady, setFramesReady] = useState(false)
  const [cgu, setCgu] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { trackEvent, pushInstruction } = useMatomo()
  const router = useRouter()
  const [error, setError] = useState(null)
  const [paymentProcessing, setPaymentProcessing] = useState(false)
  const [paymentFailed, setPaymentFailed] = useState(true)

  useEffect(() => {
    if (router?.query?.insufficient_funds) {
      setError(t('texts:funnel:payment:errors:insufficient_funds'))
    } else if (router?.query?.error_3D) {
      setError(t('texts:funnel:payment:error'))
    }
  }, [router?.query])

  const changeCgu = () => {
    setCgu(!cgu)
  }

  const onFramesReady = () => {
    setFramesReady(true)
  }

  const handleFormSubmit = (e) => {
    e.preventDefault()
    Frames.submitCard()
  }

  const cartToken = useMemo(() => cart?.item?.tokenValue, [cart])

  const handlePayment =
    (cartToken: string) => async (e: FrameCardTokenizedEvent) => {
      await createCheckoutPayment(cart.item.tokenValue)
      const email = localStorage.getItem('email')
      const linkLocal = router.locale ? `/${router.locale}` : ''

      const data = {
        email: email,
        token: e.token,
        frontSuccessPaymentUrl: `${window.location.origin}${linkLocal}/confirmation`,
        frontFailPaymentUrl: `${window.location.origin}${linkLocal}/payment?error_3D=true`,
        swik_id: swikId,
      }

      const payment: any = await postDadPayment(data, cartToken)

      if (payment?.paymentResult.status === 'Pending') {
        // Because of 3d secure we open a new window, causing an unstable redux state.
        // We set a local storage item in order to keep track of payment in confirmation page
        localStorage.setItem('Payment type', '3dSecure')
        window
          .open(payment.paymentResult?._links?.redirect?.href, '_self')
          .focus()
      } else if (payment.paymentResult === 'Order completed') {
        setPaymentProcessing(false)
        setPaymentFailed(false)
        // localStorage.removeItem('CartToken')
        // https://matomo.org/docs/ecommerce-analytics/#how-to-set-up-ecommerce-tracking-in-matomo
        await pushInstruction(
          'trackEcommerceOrder',
          cart?.item?.tokenValue, // orderId (Required) – String – A unique reference number to avoid duplication.
          numberToPrice(cart?.item?.totals?.total), // revenue (Required) – Integer/Float – The order total including tax & shipping with any discounts subtracted.
          numberToPrice(
            cart?.item?.totals?.total - cart?.item?.totals?.shipping
          ), // subTotal (Optional) – Integer/Float – The order total excluding shipping.
          numberToPrice(cart?.item?.totals?.taxes), // tax (Optional) – Integer/Float -The amount of tax charged.
          numberToPrice(cart?.item?.totals?.shipping), // shipping (Optional) – Integer/Float – The amount charged for shipping.
          numberToPrice(cart?.item?.totals?.promotion) // discount (Optional) – Integer/Float/Boolean – Discount offered? Default to false. Otherwise, you should include a numeric value.
        )

        const gaParams = {
          transaction_id: cart?.item?.tokenValue,
          currency: cart.item.currency,
          value: numberToPrice(cart?.item?.totals?.total),
          tax: numberToPrice(cart?.item?.totals?.taxes),
          shipping: numberToPrice(cart?.item?.totals?.shipping),
          items: cart.item.items.map(({ product, quantity, total }) => {
            const firstVariant = Object.values(product?.variants)[0]
            return {
              ['item_name']: product.name,
              ['item_id']: isChalhoub ? firstVariant.sku : product.code,
              ['item_variant']: isChalhoub && firstVariant.code,
              price: numberToPrice(total),
              quantity,
            }
          }),
        }

        const cleanGAParams =
          clearItemVariantFromGAParamsIfNotChalhoub(gaParams)
        gtag.tracker(GAEvent.PURCHASE, cleanGAParams)

        safeDataLayerPush(getPaymentInfoGtmData(cart, coupon, 'checkout'))

        // Meta Pixel: Purchase event with amount and currency
        if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
          window.fbq('track', 'Purchase', {
            value: numberToPrice(cart?.item?.totals?.total),
            currency: cart?.item?.currency,
          })
        }

        await router.push('/confirmation')
      } else {
        const error = payment?.err?.response?.data?.error
        if (error === 'some products are not available in stocks') {
          let productName = ''
          cart?.item?.items.map((products) => {
            productName = products.product.variants.find(
              (variants) =>
                variants.code ===
                payment?.err?.response?.data?.unavailableProductVariants[0]
            ).name
          })
          setError(`${t('texts:funnel:payment:errors:stock')} : ${productName}`)
        } else {
          if (error?.code === 'card_declined') {
            switch (error?.decline_code) {
              case 'insufficient_funds':
                setError(t('texts:funnel:payment:errors:insufficient_funds'))
                break
              case 'lost_card':
                setError(t('texts:funnel:payment:errors:lost_card'))
                break
              case 'stolen_card':
                setError(t('texts:funnel:payment:errors:stolen_card'))
                break
              case 'Invalid email address':
                setError(t('texts:funnel:payment:errors:email'))
                break
              default:
                setError(t('texts:funnel:payment:errors:default'))
                break
            }
          } else {
            switch (error?.code) {
              case 'expired_card':
                setError(t('texts:funnel:payment:errors:expired_card'))
                break
              case 'incorrect_cvc':
                setError(t('texts:funnel:payment:errors:incorrect_cvc'))
                break
              case 'processing_error':
                setError(t('texts:funnel:payment:errors:processing_error'))
                break
              default:
                setError(t('texts:funnel:payment:errors:default'))
                break
            }
          }
        }
        setPaymentProcessing(false)
        setPaymentFailed(true)
      }
    }

  return !cartToken ? (
    <div className={classes.loaderContainer}>
      <Loader />
    </div>
  ) : (
    <Frames
      ready={onFramesReady}
      config={{
        publicKey,
        schemeChoice: true,
        style: framesStyles,
        localization: {
          cardNumberPlaceholder: '1234 1234 1234 1234',
          expiryMonthPlaceholder: 'MM',
          expiryYearPlaceholder: 'AA',
          cvvPlaceholder: 'CVC',
        },
      }}
      cardTokenized={handlePayment(cartToken)}
    >
      <form className={commonClasses.form}>
        <div className={classes.formContainer}>
          <div className={clsx('label', commonClasses.label)}>
            <CMSText
              asText
              data={settings?.card_number}
              defaultText={t('texts:general:card_number')}
            />
          </div>
          <CardNumber
            className={clsx(classes.inputField, 'input', commonClasses.input)}
            placeholder="1234 1234 1234 1234"
          />
          <Grid container justify={'space-between'}>
            <Grid xs={5} item>
              <div className={clsx('label', commonClasses.label)}>
                <CMSText
                  asText
                  data={settings?.expiry_date}
                  defaultText={t('texts:general:expiry_date')}
                />
              </div>
              <ExpiryDate
                className={clsx(
                  classes.inputField,
                  'input',
                  commonClasses.input
                )}
              />
            </Grid>
            <Grid xs={5} item className="cb-infos">
              <div className={clsx('label', commonClasses.label)}>
                <CMSText
                  asText
                  data={settings?.cvc_code}
                  defaultText={t('texts:general:cvc_code')}
                />
              </div>

              <Cvv
                className={clsx(
                  classes.inputField,
                  'input',
                  commonClasses.input
                )}
              />
            </Grid>
          </Grid>

          <FormControlLabel
            control={
              <Checkbox
                checked={cgu}
                onChange={changeCgu}
                name="checkedA"
                data-testid={'cgu_check'}
              />
            }
            label={
              <Typography
                variant={'body2'}
                component={'span'}
                className={commonClasses.cgu}
              >
                <CMSText
                  data={text?.cgu}
                  defaultText={t('texts:funnel:payment:cgu')}
                />
              </Typography>
            }
          />
          {paymentFailed && (
            <Typography
              color={'error'}
              variant={'caption'}
              align={'center'}
              component="div"
            >
              {error}
            </Typography>
          )}

          <div className={classes.payButtonContainer}>
            <Button
              disabled={
                !cgu || !Frames.isCardValid() || !framesReady || disabled
              }
              className={clsx(
                classes.payButton,
                commonClasses.submit,
                {
                  [commonClasses.inProgress]: paymentProcessing,
                  'payment-form__submit-progress': paymentProcessing,
                },
                'payment-form__submit'
              )}
              variant="contained"
              data-testid="paymentSubmit"
              color="primary"
              onClick={handleFormSubmit}
              type="submit"
            >
              <span className={commonClasses.show}>
                <CMSText
                  asText
                  data={text?.pay}
                  defaultText={t('texts:funnel:pay')}
                />
              </span>
            </Button>
          </div>
        </div>
      </form>
    </Frames>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(PaymentFormCheckout)
